// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../config/api';

function ProgressView({ topic, token }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!token) return;
        setLoading(true);
        setError('');

        fetch(`${API_BASE_URL}/learnwithai/ai/progress?topic=${encodeURIComponent(topic)}`, {
            headers: { 'Authorization': `Bearer ${token}` },
        })
            .then(r => r.text())
            .then(text => {
                const trimmed = text ? text.trim() : '';
                if (!trimmed || trimmed === 'undefined') return;
                const data = JSON.parse(trimmed);
                if (data.success) setStats(data.data);
                else setError(data.message || 'Failed to load progress.');
            })
            .catch(() => setError('Failed to load progress data.'))
            .finally(() => setLoading(false));
    }, [topic, token]);

    if (loading) {
        return (
            <div className="lw-empty">
                <span className="lw-empty-icon">⏳</span>
                <p>Loading your progress…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="lw-empty">
                <div className="lw-error">{error}</div>
            </div>
        );
    }

    if (!stats || stats.total === 0) {
        return (
            <div className="lw-empty">
                <span className="lw-empty-icon">📊</span>
                <p>No activity yet for <strong>{topic}</strong>. Start chatting, doing quizzes, or reviewing flashcards!</p>
            </div>
        );
    }

    const maxTrend = Math.max(...Object.values(stats.weeklyTrend || {}), 1);

    return (
        <div className="lw-progress">
            {/* Streak & Summary Cards */}
            <div className="lw-progress-cards">
                <div className="lw-stat-card lw-stat-streak">
                    <div className="lw-stat-icon">🔥</div>
                    <div className="lw-stat-value">{stats.currentStreak || 0}</div>
                    <div className="lw-stat-label">Day Streak</div>
                </div>
                <div className="lw-stat-card">
                    <div className="lw-stat-icon">💬</div>
                    <div className="lw-stat-value">{stats.total}</div>
                    <div className="lw-stat-label">Total Interactions</div>
                </div>
                <div className="lw-stat-card">
                    <div className="lw-stat-icon">🎯</div>
                    <div className="lw-stat-value">{stats.quiz.accuracy}%</div>
                    <div className="lw-stat-label">Quiz Accuracy</div>
                </div>
                <div className="lw-stat-card">
                    <div className="lw-stat-icon">⚡</div>
                    <div className="lw-stat-value">{stats.recentActivity}</div>
                    <div className="lw-stat-label">This Week</div>
                </div>
            </div>

            {/* Activity Breakdown */}
            <div className="lw-progress-section">
                <h3 className="lw-progress-heading">Activity Breakdown</h3>
                <div className="lw-activity-bars">
                    {[
                        { label: 'Chat Questions', count: stats.byType.hover_questions, color: '#47b5ff' },
                        { label: 'Flashcards', count: stats.byType.flashcards, color: '#a78bfa' },
                        { label: 'Quiz Attempts', count: stats.byType.quiz_attempts, color: '#34d399' },
                        { label: 'Hints Used', count: stats.byType.hints, color: '#fbbf24' },
                    ].map(item => {
                        const pct = stats.total > 0 ? Math.round((item.count / stats.total) * 100) : 0;
                        return (
                            <div key={item.label} className="lw-activity-row">
                                <span className="lw-activity-label">{item.label}</span>
                                <div className="lw-activity-bar-track">
                                    <div
                                        className="lw-activity-bar-fill"
                                        style={{ width: `${pct}%`, background: item.color }}
                                    />
                                </div>
                                <span className="lw-activity-count">{item.count}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Weekly Trend */}
            {stats.weeklyTrend && (
                <div className="lw-progress-section">
                    <h3 className="lw-progress-heading">7-Day Activity</h3>
                    <div className="lw-trend-chart">
                        {Object.entries(stats.weeklyTrend).map(([date, count]) => {
                            const height = Math.max((count / maxTrend) * 100, 4);
                            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                            const day = dayNames[new Date(date + 'T00:00:00').getDay()];
                            return (
                                <div key={date} className="lw-trend-col">
                                    <div className="lw-trend-bar-wrap">
                                        <div
                                            className="lw-trend-bar"
                                            style={{ height: `${height}%` }}
                                            title={`${date}: ${count} activities`}
                                        />
                                    </div>
                                    <span className="lw-trend-day">{day}</span>
                                    <span className="lw-trend-count">{count}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Quiz Performance */}
            {stats.quiz.total > 0 && (
                <div className="lw-progress-section">
                    <h3 className="lw-progress-heading">Quiz Performance</h3>
                    <div className="lw-quiz-ring-wrap">
                        <div className="lw-quiz-ring">
                            <svg viewBox="0 0 36 36" className="lw-ring-svg">
                                <path
                                    className="lw-ring-bg"
                                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                />
                                <path
                                    className="lw-ring-fg"
                                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                    strokeDasharray={`${stats.quiz.accuracy}, 100`}
                                    strokeLinecap="round"
                                />
                            </svg>
                            <div className="lw-ring-text">
                                <span className="lw-ring-pct">{stats.quiz.accuracy}%</span>
                                <span className="lw-ring-sub">accuracy</span>
                            </div>
                        </div>
                        <div className="lw-quiz-stats-detail">
                            <div><strong>{stats.quiz.correct}</strong> correct out of <strong>{stats.quiz.total}</strong> attempts</div>
                            {stats.avgFlashcardConfidence && (
                                <div>Avg. flashcard confidence: <strong>{stats.avgFlashcardConfidence}/5</strong></div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Topic Mastery */}
            {stats.topicMastery && stats.topicMastery.length > 1 && (
                <div className="lw-progress-section">
                    <h3 className="lw-progress-heading">Topic Mastery</h3>
                    <div className="lw-mastery-grid">
                        {stats.topicMastery.map(tm => (
                            <div key={tm.topic} className="lw-mastery-item">
                                <div className="lw-mastery-topic">{tm.topic}</div>
                                <div className="lw-mastery-meta">
                                    <span>{tm.interactions} interactions</span>
                                    {tm.quizAccuracy != null && <span> · {tm.quizAccuracy}% quiz accuracy</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProgressView;
